import React, { useEffect, useState } from "react";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import { url } from "../../../redux/config";
import axios from "axios";
import AddButton from "../../buttons/button";
import DownloadIcon2 from "../../../assets/icons/ABHA-icons/download2-icon";
import PrintIcon2 from "../../../assets/icons/ABHA-icons/print-icon2";

function Last({
  adhaarX_Token,
  adhaarAccessToken,
  adhaarSuccessData,
  closeModal,
}) {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [abhaCardImage, setAbhaCardImage] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Function to fetch ABHA card and convert to image URL
  const fetchABHACard = async () => {
    try {
      setInitialLoading(true);
      const response = await axios.post(
        `${url}/abha-integration/abha-download`,
        {
          x_token: adhaarX_Token,
          accessToken: adhaarAccessToken,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob", // Handle binary data for ABHA card download
        }
      );

      console.log("Response:", response);

      // Create blob URL for display
      const blob = new Blob([response.data], { type: "image/png" });
      const imageUrl = URL.createObjectURL(blob);

      setAbhaCardImage(imageUrl);
      setInitialLoading(false);

      return response.data;
    } catch (error) {
      errorToast(error.response?.data?.message || "Failed to load ABHA card");
      console.error(
        "Error fetching ABHA card:",
        error.response?.data || error.message
      );
      setInitialLoading(false);
    }
  };

  // Load ABHA card when component mounts
  useEffect(() => {
    if (adhaarX_Token && adhaarAccessToken) {
      fetchABHACard();
    }

    // Cleanup function to revoke object URL
    return () => {
      if (abhaCardImage) {
        URL.revokeObjectURL(abhaCardImage);
      }
    };
  }, [adhaarX_Token, adhaarAccessToken]);

  const downloadABHA = async () => {
    try {
      setDownloadLoading(true);
      const response = await axios.post(
        `${url}/abha-integration/abha-download`,
        {
          x_token: adhaarX_Token,
          accessToken: adhaarAccessToken,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob", // Handle binary data for ABHA card download
        }
      );

      console.log("Response:", response);

      // Create blob and download immediately
      const blob = new Blob([response.data], { type: "image/png" });
      const imageUrl = URL.createObjectURL(blob);

      // Create download link and trigger download
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `ABHA-Card-${
        adhaarSuccessData?.ABHANumber || Date.now()
      }.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(imageUrl);

      successToast("ABHA Card Downloaded Successfully");
      setDownloadLoading(false);

      return response.data;
    } catch (error) {
      errorToast(error.response?.data?.message || "Download failed");
      console.error(
        "Error downloading ABHA:",
        error.response?.data || error.message
      );
      setDownloadLoading(false);
    }
  };

  const printABHA = () => {
    if (abhaCardImage) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>ABHA Card - ${adhaarSuccessData?.name || "Print"}</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              @media print {
                body { padding: 0; }
              }
            </style>
          </head>
          <body>
            <img src="${abhaCardImage}" alt="ABHA Card" />
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } else {
      errorToast("ABHA card not loaded yet");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 mt-6 h-full flex flex-col gap-6">
        {/* Header Section */}
        <div className="flex flex-col gap-3 text-center mt-6">
          <label
            className="text-[var(--primary)]
              text-[length:var(--abha-method-title2-fs)] font-[var(--abha-method-title-fw)] whitespace-nowrap"
          >
            Welcome, {adhaarSuccessData?.name || "Buridi Ramakrishna"}
          </label>

          <label
            className="text-[var(--abha-nav-fg)]
              text-[length:var(--abha-method-card-sub-title-fs)] font-[var(--abha-method-card-sub-title-fw)] whitespace-nowrap"
          >
            View and manage your Ayushman Bharat Health Account card.
          </label>
        </div>

        {/* ABHA Card Display Section */}
        <div className="flex-1 flex justify-center items-center bg-gray-50 rounded-lg p-4">
          {initialLoading ? (
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
              <p className="text-[var(--abha-nav-fg)] text-sm">
                Loading ABHA Card...
              </p>
            </div>
          ) : abhaCardImage ? (
            <div className="max-w-full max-h-full flex justify-center">
              <img
                src={abhaCardImage}
                alt="ABHA Card"
                className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                style={{ maxHeight: "400px" }}
              />
            </div>
          ) : (
            <div className="text-center">
              <p className="text-[var(--abha-nav-fg)] mb-4">
                Failed to load ABHA Card
              </p>
              <button
                onClick={fetchABHACard}
                className="px-4 py-2 bg-[var(--primary)] text-white rounded-md hover:opacity-90"
              >
                Retry
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-row gap-2 justify-center">
          <button
            onClick={downloadABHA}
            disabled={downloadLoading || !abhaCardImage}
            className="flex flex-row items-center px-3 py-2 bg-[var(--abha-caution-bg)] whitespace-nowrap rounded-md gap-[8px]
              cursor-pointer text-[var(--primary-button-bg-color)] border border-[var(--primary-button-bg-color)]
              text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)]
              hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloadLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            ) : (
              <DownloadIcon2 />
            )}
            <label className="cursor-pointer">
              {downloadLoading ? "Downloading..." : "Download"}
            </label>
          </button>

          <button
            onClick={printABHA}
            disabled={!abhaCardImage}
            className="flex flex-row items-center px-3 py-2 bg-[var(--abha-caution-bg)] whitespace-nowrap rounded-md gap-[8px]
              cursor-pointer text-[var(--primary-button-bg-color)] border border-[var(--primary-button-bg-color)]
              text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)]
              hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PrintIcon2 />
            <label className="cursor-pointer">Print ABHA</label>
          </button>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{ bottom: "0" }}
        className="flex flex-row gap-3 justify-end sticky bg-white py-4 mt-auto"
      >
        <AddButton
          prefixIcon={false}
          text={"Close"}
          onclkFn={() => closeModal()}
          className="flex items-center px-3 py-2 whitespace-nowrap rounded-md gap-[8px]
            bg-[var(--trans-stats-con-red-fg)] cursor-pointer text-white
            text-[length:var(--draft-button-fs)] font-[var(--draft-button-fw)]"
          type="button"
        />
      </div>
    </div>
  );
}

// function Last({
//   adhaarX_Token,
//   adhaarAccessToken,
//   adhaarSuccessData,
//   closeModal,
// }) {
//   const [loading, setLoading] = useState(false);

//   const downloadABHA = async () => {
//     try {
//       setLoading(true);
//       const response = await axios.post(
//         `${url}/abha-integration/abha-download`,
//         {
//           x_token: adhaarX_Token,
//           accessToken: adhaarAccessToken,
//         },
//         {
//           headers: {
//             "Content-Type": "application/json",
//           },
//           responseType: "blob", // Handle binary data for ABHA card download
//         }
//       );

//       console.log("Response:", response);

//       // Create blob and download immediately
//       const blob = new Blob([response.data], { type: "image/png" }); // or 'image/png' depending on your API
//       const imageUrl = URL.createObjectURL(blob);

//       // Create download link and trigger download
//       const link = document.createElement("a");
//       link.href = imageUrl;
//       link.download = `ABHA-Card-${
//         adhaarSuccessData?.ABHANumber || Date.now()
//       }.png`; // or .png
//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);
//       URL.revokeObjectURL(imageUrl);

//       successToast("ABHA Card Downloaded Successfully");
//       setLoading(false);

//       return response.data;
//     } catch (error) {
//       errorToast(error.response?.data || error.message);
//       console.error(
//         "Error downloading ABHA:",
//         error.response?.data || error.message
//       );
//       setLoading(false);
//     }
//   };
//   return (
//     <div className="flex flex-col h-full">
//       <div className="flex justify-center items-center h-full">
//         <div className="flex flex-col gap-3 ">
//           <label
//             className={` text-[var(--primary)]
//                 text-[length:var(--abha-method-title2-fs)] font-[var(--abha-method-title-fw)] whitespace-nowrap`}
//           >
//             Welcome, Buridi Ramakrishna
//           </label>

//           <label
//             className={`text-[var(--abha-nav-fg)]
//                  text-[length:var(--abha-method-card-sub-title-fs)] font-[var(--abha-method-card-sub-title-fw)] whitespace-nowrap`}
//           >
//             View and manage your Ayushman Bharat Health Account card.
//           </label>
//         </div>

//         <div className="flex flex-row gap-2">
//           <div
//             className="
//     flex  flex-row items-center px-3 py-2  bg-[var(--abha-caution-bg)] whitespace-nowrap rounded-md gap-[8px]
//      cursor-pointer
//     text-[var(--primary-button-bg-color)]
//     border border-[var(--primary-button-bg-color)]
//     text-[length:var(--primary-button-create-fs)]
//     font-[var(--primary-button-font-weight)]
//     "
//           >
//             <DownloadIcon2 />
//             <label>Download</label>
//           </div>
//           <div
//             className="
//     flex  flex-row items-center px-3 py-2  bg-[var(--abha-caution-bg)] whitespace-nowrap rounded-md gap-[8px]
//      cursor-pointer
//     text-[var(--primary-button-bg-color)]
//     border border-[var(--primary-button-bg-color)]
//     text-[length:var(--primary-button-create-fs)]
//     font-[var(--primary-button-font-weight)]
//     "
//           >
//             <PrintIcon2 />
//             <label>Print ABHA</label>
//           </div>
//         </div>
//       </div>
//       <div
//         style={{
//           bottom: "0",
//         }}
//         className="flex flex-row gap-3 justify-end  sticky bg-white py-4 mt-auto "
//       >
//         <AddButton
//           prefixIcon={false}
//           text={"Cancel"}
//           onclkFn={() => closeModal()}
//           className="
//     flex items-center px-3 py-2  whitespace-nowrap rounded-md gap-[8px]
//     bg-[var(--trans-stats-con-red-fg)] cursor-pointer
//     text-white
//     text-[length:var(--draft-button-fs)]
//     font-[var(--draft-button-fw)]
//     "
//           type="button"
//         />
//       </div>
//     </div>
//   );
// }

export default Last;
